// Storage management utilities for AltTextify Ecwid app

// Storage keys
export const STORAGE_KEYS = {
  STORE_ID: 'alttextify_store_id',
  API_KEY: 'alttextify_api_key',
  SETTINGS: 'alttextify_settings',
  SESSION_TIMESTAMP: 'session_timestamp', // Keep in localStorage for persistence
  ECWID_AUTH: 'ecwid_auth'
};

// Session duration (24 hours)
export const SESSION_DURATION = 24 * 60 * 60 * 1000;

/**
 * Check if session is expired
 * @param {string|number} timestamp - Session timestamp
 * @returns {boolean} - True if session is expired
 */
export const isSessionExpired = (timestamp) => {
  if (!timestamp) return true;
  const now = Date.now();
  const sessionTime = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
  return (now - sessionTime) > SESSION_DURATION;
};

/**
 * Clear all stored data (logout)
 */
export const clearAllStorage = () => {
  try {
    // Clear sessionStorage
    sessionStorage.removeItem(STORAGE_KEYS.ECWID_AUTH);
    sessionStorage.removeItem(STORAGE_KEYS.STORE_ID);
    sessionStorage.removeItem(STORAGE_KEYS.API_KEY);
    sessionStorage.removeItem(STORAGE_KEYS.SETTINGS);
    
    // Clear localStorage (only session timestamp)
    localStorage.removeItem(STORAGE_KEYS.SESSION_TIMESTAMP);
    
    console.log('All storage cleared successfully');
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
};

/**
 * Clear expired session data
 */
export const clearExpiredSession = () => {
  try {
    // Clear sessionStorage
    sessionStorage.removeItem(STORAGE_KEYS.ECWID_AUTH);
    sessionStorage.removeItem(STORAGE_KEYS.STORE_ID);
    sessionStorage.removeItem(STORAGE_KEYS.API_KEY);
    sessionStorage.removeItem(STORAGE_KEYS.SETTINGS);
    
    // Clear localStorage (only session timestamp)
    localStorage.removeItem(STORAGE_KEYS.SESSION_TIMESTAMP);
    
    console.log('Expired session cleared');
  } catch (error) {
    console.error('Error clearing expired session:', error);
  }
};

/**
 * Set session timestamp (keep in localStorage for persistence)
 */
export const setSessionTimestamp = () => {
  try {
    localStorage.setItem(STORAGE_KEYS.SESSION_TIMESTAMP, Date.now().toString());
  } catch (error) {
    console.error('Error setting session timestamp:', error);
  }
};

/**
 * Get session timestamp (from localStorage)
 * @returns {number|null} - Session timestamp or null
 */
export const getSessionTimestamp = () => {
  try {
    const timestamp = localStorage.getItem(STORAGE_KEYS.SESSION_TIMESTAMP);
    return timestamp ? parseInt(timestamp) : null;
  } catch (error) {
    console.error('Error getting session timestamp:', error);
    return null;
  }
};

/**
 * Check if current session is valid
 * @returns {boolean} - True if session is valid
 */
export const isSessionValid = () => {
  const timestamp = getSessionTimestamp();
  return !isSessionExpired(timestamp);
};

/**
 * Get store ID from sessionStorage
 * @returns {string|null} - Store ID or null
 */
export const getStoreId = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.STORE_ID);
  } catch (error) {
    console.error('Error getting store ID:', error);
    return null;
  }
};

/**
 * Set store ID in sessionStorage
 * @param {string} storeId - Store ID to store
 */
export const setStoreId = (storeId) => {
  try {
    if (storeId) {
      sessionStorage.setItem(STORAGE_KEYS.STORE_ID, storeId);
    }
  } catch (error) {
    console.error('Error setting store ID:', error);
  }
};

/**
 * Get API key from sessionStorage
 * @returns {string|null} - API key or null
 */
export const getApiKey = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.API_KEY);
  } catch (error) {
    console.error('Error getting API key:', error);
    return null;
  }
};

/**
 * Set API key in sessionStorage
 * @param {string} apiKey - API key to store
 */
export const setApiKey = (apiKey) => {
  try {
    if (apiKey) {
      sessionStorage.setItem(STORAGE_KEYS.API_KEY, apiKey);
    }
  } catch (error) {
    console.error('Error setting API key:', error);
  }
};

/**
 * Get settings from sessionStorage
 * @returns {object|null} - Settings object or null
 */
export const getSettings = () => {
  try {
    const settings = sessionStorage.getItem(STORAGE_KEYS.SETTINGS);
    return settings ? JSON.parse(settings) : null;
  } catch (error) {
    console.error('Error getting settings:', error);
    return null;
  }
};

/**
 * Set settings in sessionStorage
 * @param {object} settings - Settings object to store
 */
export const setSettings = (settings) => {
  try {
    if (settings) {
      sessionStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
    }
  } catch (error) {
    console.error('Error setting settings:', error);
  }
};

/**
 * Get Ecwid auth data from sessionStorage
 * @returns {object|null} - Auth data object or null
 */
export const getEcwidAuth = () => {
  try {
    const auth = sessionStorage.getItem(STORAGE_KEYS.ECWID_AUTH);
    return auth ? JSON.parse(auth) : null;
  } catch (error) {
    console.error('Error getting Ecwid auth:', error);
    return null;
  }
};

/**
 * Set Ecwid auth data in sessionStorage
 * @param {object} authData - Auth data object to store
 */
export const setEcwidAuth = (authData) => {
  try {
    if (authData) {
      sessionStorage.setItem(STORAGE_KEYS.ECWID_AUTH, JSON.stringify(authData));
    }
  } catch (error) {
    console.error('Error setting Ecwid auth:', error);
  }
};

/**
 * Initialize session with timestamp
 */
export const initializeSession = () => {
  setSessionTimestamp();
};

/**
 * Validate and refresh session if needed
 * @returns {boolean} - True if session is valid
 */
export const validateSession = () => {
  if (!isSessionValid()) {
    clearExpiredSession();
    return false;
  }
  return true;
}; 